import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  Button,
  cn,
  LeftCard,
  RightCard,
  TextSeparator,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  SocialProviderType,
  useCognito,
} from '@sandtech-hub-monorepo/ui-auth-kit';

import { SignInForm } from './components/SignInForm';
import { FitSsoSignIn } from './components/FitSsoSignIn';
import { socialGridColumnsClassName } from '../socialButtonsGrid';
import {
  signInTitle as defaultSignInTitle,
  signInSubtitle as defaultSignInSubtitle,
  signInCTAQuestion,
  signInCTAText as defaultSignInCTAText,
} from '../../../../stringsManager';
import React from 'react';

export interface SignInProps {
  resetPasswordRoute: string;
  signUpRoute?: string;
  confirmSignUpRoute: string;
  homeRoute: string;
  updatePasswordRoute: string;
  supportRoute?: string;
  loginCover: string;
  /**
   * Rendered in the top-left corner of the LeftCard, over the cover image.
   */
  loginCoverLogo?: React.ReactNode;
  /**
   * Adds a dark gradient scrim over the cover image, for photos that don't
   * have enough natural contrast for loginCoverLogo to stay legible. Defaults
   * to a top-to-bottom black gradient; pass loginCoverOverlayClassName for a
   * different one.
   */
  loginCoverOverlay?: boolean;
  loginCoverOverlayClassName?: string;
  /**
   * Anchors loginCoverLogo to the top or bottom of the cover. Defaults to the
   * top.
   */
  loginCoverLogoPosition?: 'top' | 'bottom';
  /**
   * Renders a compact solid-color banner carrying loginCoverLogo, visible
   * only below `lg` (where the cover photo is otherwise hidden entirely).
   * Undefined (the default) renders nothing, matching every existing
   * caller's current mobile behavior.
   */
  loginCoverMobileHeaderClassName?: string;
  signInWithGoogleLogo?: React.ReactNode;
  signInWithFacebookLogo?: React.ReactNode;
  signInWithLinkedInLogo?: React.ReactNode;
  signInWithEhubLogo?: React.ReactNode;
  /**
   * Fired after a successful email+password sign in (nextStep === 'DONE').
   * Not called for MFA / new-password-required / unconfirmed flows.
   * Social sign in success is reported via CognitoProvider's onSocialSignIn.
   */
  onSignInSuccess?: () => void;
  /**
   * When true, hides the email/password sign in form (and its separators)
   * and renders only the eHub sign in button, plus a required terms/privacy
   * consent checkbox. Only intended for the FIT (set-edu) platform.
   */
  disablePasswordSignIn?: boolean;
  /**
   * Overrides the heading/subtitle/CTA copy. Default to the shared strings,
   * so other apps are unaffected. FIT passes "Sign In".
   */
  signInTitle?: string;
  signInSubtitle?: string;
  signInCTAText?: string;
  /**
   * Replaces the default per-provider social button styling with one shared
   * class, so every provider (including eHub) renders identically.
   */
  socialButtonsClassName?: string;
  /**
   * Lays the social buttons out as equal-width columns instead of shrinking
   * each one to its logo.
   */
  evenSocialButtons?: boolean;
  /**
   * Overrides the grid-cols-* class evenSocialButtons picks by count (e.g.
   * for a responsive 2-cols-on-mobile/4-on-desktop layout). Only applies
   * when evenSocialButtons is set. Undefined keeps today's single fixed
   * column count for every existing caller.
   */
  socialGridClassName?: string;
  /**
   * Overrides the identifier field label (e.g. "Email or Student ID"); when
   * set, the field also accepts a non-empty identifier instead of a strict
   * email. Passed through to SignInForm.
   */
  identifierLabel?: string;
  /**
   * Every prop below only exists to let the FIT (set-edu) redesign restyle
   * this shared page without touching a hardcoded class any other caller
   * (central-hub, staff-hub, cities-ui, attendance-ui) relies on. Each
   * defaults to today's literal class string, so omitting the prop keeps
   * today's exact output.
   */
  /** The "Sign In"/"Welcome back!" heading. */
  titleClassName?: string;
  /** Wrapper (not text) class for the "Social sign in options" separator. */
  socialSeparatorClassName?: string;
  /** Text class for the "Social sign in options" separator. */
  socialSeparatorTextClassName?: string;
  /** Wrapper (not text) class for the "Manual sign in" separator. */
  formSeparatorClassName?: string;
  /** Text class for the "Manual sign in" separator. */
  formSeparatorTextClassName?: string;
  /** Passed through to SignInForm's submit button. */
  signInButtonClassName?: string;
  /** Passed through to SignInForm's two field labels. */
  identifierLabelClassName?: string;
  /** Passed through to SignInForm's email/password Input fields. */
  identifierInputClassName?: string;
  /** Passed through to SignInForm's "Forgot password" link. */
  forgotPasswordLinkClassName?: string;
  /**
   * Extra classes on RightCard's inner content column. Passed through to
   * RightCard - see its prop doc. Defaults to nothing extra, so every
   * existing caller keeps today's exact layout.
   */
  rightCardClassName?: string;
  /** The "Don't have an account?" row - question text. */
  ctaClassName?: string;
  /** The "Don't have an account?" row - "Sign up" link. */
  ctaLinkClassName?: string;
  /** The "Struggling to log in..." row - question text. */
  supportClassName?: string;
  /** The "Struggling to log in..." row - "Click here to contact us" line. */
  supportBodyClassName?: string;
  /** The "Struggling to log in..." row - "Click here" link. */
  supportLinkClassName?: string;
}

export const SignIn = (props: SignInProps) => {
  const { signIn, socialSignIn, currentUser, loading } = useCognito();

  const navigate = useNavigate();
  const [params] = useSearchParams();

  if (currentUser) {
    navigate({
      pathname: props.homeRoute,
    });
  }

  const handleUsernamePasswordSignIn = async (values: {
    email: string;
    password: string;
  }) => {
    const result = await signIn({
      username: values.email,
      password: values.password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });

    if (result) {
      switch (result.nextStep.signInStep) {
        case 'CONFIRM_SIGN_UP':
          navigate({
            pathname: props.confirmSignUpRoute,
            search: `email=${encodeURIComponent(values.email)}`,
          });
          toast(
            'Your account has not yet been activated. Please check your inbox (and spam folder) for the confirmation email.'
          );
          break;
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          navigate({
            pathname: props.updatePasswordRoute,
          });
          break;
        case 'DONE':
          props.onSignInSuccess?.();
          break;
        default:
      }
    }
  };

  const handleGoogleSignIn = () => {
    socialSignIn('Google');
  };

  const handleFacebookSignIn = () => {
    socialSignIn('Facebook');
  };

  const handleLinkedInSignIn = () => {
    socialSignIn({ custom: 'LinkedIn' } as SocialProviderType);
  };

  const handleEhubSignIn = () => {
    socialSignIn({ custom: 'Ehub' } as SocialProviderType);
  };

  const socialLogos = [
    props.signInWithGoogleLogo,
    props.signInWithLinkedInLogo,
    props.signInWithFacebookLogo,
    props.signInWithEhubLogo,
  ].filter((x) => !!x);
  const socialRowClassName = props.evenSocialButtons
    ? cn(
        'grid gap-4',
        socialGridColumnsClassName(socialLogos.length),
        props.socialGridClassName
      )
    : 'flex gap-4';
  const socialButtonClassName = cn(
    'w-full',
    props.socialButtonsClassName ??
      'bg-background text-gray-700 border active:bg-primary hover:bg-card sm:hover:bg-background-tertiary'
  );
  const ehubButtonClassName = cn(
    'w-full',
    props.socialButtonsClassName ??
      'border-[rgb(2,157,224)] bg-transparent text-[rgb(2,157,224)] hover:text-white border active:text-white active:bg-[rgb(5,140,224)] hover:bg-[rgb(2,157,224)]'
  );

  return (
    <>
      <LeftCard
        backgroundImage={props.loginCover}
        logo={props.loginCoverLogo}
        overlay={props.loginCoverOverlay}
        overlayClassName={props.loginCoverOverlayClassName}
        logoPosition={props.loginCoverLogoPosition}
        mobileHeaderClassName={props.loginCoverMobileHeaderClassName}
      />
      <RightCard className={props.rightCardClassName}>
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" />
            Loading ...
          </div>
        ) : props.disablePasswordSignIn ? (
          <FitSsoSignIn
            onEhubSignIn={handleEhubSignIn}
            loading={loading}
            signInWithEhubLogo={props.signInWithEhubLogo}
          />
        ) : (
          <>
            <div className="flex flex-col space-y-2">
              <h1
                className={cn(
                  'text-2xl font-semibold tracking-tight',
                  props.titleClassName
                )}
              >
                {props.signInTitle ?? defaultSignInTitle}
              </h1>
              <p className="text-sm text-muted-foreground">
                {props.signInSubtitle ?? defaultSignInSubtitle}
              </p>
            </div>
            {[
              props.signInWithGoogleLogo,
              props.signInWithLinkedInLogo,
              props.signInWithFacebookLogo,
              props.signInWithEhubLogo,
            ].some((x) => !!x) && (
              <TextSeparator
                text="Social sign in options"
                className={props.socialSeparatorClassName ?? 'font-semibold'}
                textClassName={props.socialSeparatorTextClassName}
              />
            )}
            <div className={socialRowClassName}>
              {props.signInWithGoogleLogo ? (
                <Button
                  onClick={handleGoogleSignIn}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signInWithGoogleLogo}
                </Button>
              ) : null}
              {props.signInWithLinkedInLogo ? (
                <Button
                  onClick={handleLinkedInSignIn}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signInWithLinkedInLogo}
                </Button>
              ) : null}
              {props.signInWithFacebookLogo ? (
                <Button
                  onClick={handleFacebookSignIn}
                  className={socialButtonClassName}
                  disabled={loading}
                >
                  {props.signInWithFacebookLogo}
                </Button>
              ) : null}
              {props.signInWithEhubLogo && (
                <Button
                  onClick={handleEhubSignIn}
                  className={ehubButtonClassName}
                  disabled={loading}
                >
                  {props.signInWithEhubLogo}
                </Button>
              )}
            </div>
            {[
              props.signInWithGoogleLogo,
              props.signInWithLinkedInLogo,
              props.signInWithFacebookLogo,
              props.signInWithEhubLogo,
            ].some((x) => !!x) && (
              <TextSeparator
                text="Manual sign in"
                className={props.formSeparatorClassName ?? 'font-semibold'}
                textClassName={props.formSeparatorTextClassName}
              />
            )}
            <SignInForm
              onSubmit={handleUsernamePasswordSignIn}
              loading={loading}
              resetPasswordRoute={props.resetPasswordRoute}
              defaultValues={{
                email: decodeURIComponent(params.get('email') || ''),
              }}
              searchParams={params}
              identifierLabel={props.identifierLabel}
              submitButtonClassName={props.signInButtonClassName}
              labelClassName={props.identifierLabelClassName}
              inputClassName={props.identifierInputClassName}
              forgotPasswordLinkClassName={props.forgotPasswordLinkClassName}
            />
            {props.signUpRoute ? (
              <div className="flex gap-1 leading-tight justify-center items-center">
                <p
                  className={cn(
                    'text-sm text-muted-foreground',
                    props.ctaClassName
                  )}
                >
                  {signInCTAQuestion}
                </p>
                <Link
                  to={props.signUpRoute}
                  className={cn(
                    'text-sm text-primary leading-tight',
                    props.ctaLinkClassName
                  )}
                >
                  {props.signInCTAText ?? defaultSignInCTAText}
                </Link>
              </div>
            ) : null}

            {props.supportRoute ? (
              <div className="flex flex-col items-center text-sm">
                <p
                  className={cn(
                    'text-card-foreground font-medium',
                    props.supportClassName
                  )}
                >
                  Struggling to log in or sign up?
                </p>
                <p
                  className={cn(
                    'text-muted-foreground',
                    props.supportBodyClassName
                  )}
                >
                  <Link
                    to={props.supportRoute}
                    className={cn(
                      'text-sm text-primary leading-tight',
                      props.supportLinkClassName
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click here
                  </Link>{' '}
                  to contact us
                </p>
              </div>
            ) : null}
          </>
        )}
      </RightCard>
    </>
  );
};
